// offline/networkVerdict.js — EL DICTAMEN DE RED, y nada más (F6.7b, fase 2).
//
// El problema que cierra (censo a del sprint): hoy CADA pantalla decide sola si
// hay señal, y lo decide UNA vez. El Dashboard lo deduce de que su fetch cayó
// con forma-de-red (Dashboard.js:235 `offlineData`), el Centro de Estudio lo
// hereda del `offline: true` que le puso el ensamblador de bodega
// (StudyCenter.js:72), y Flashcards lo recibe congelado en el state de
// navegación (Flashcards.js:161). Ninguno se entera de que la señal volvió —
// ni de que se fue— sin navegar. Eso es lo que hace que el chip "Modo offline"
// se quede pegado y que un control deshabilitado no reviva solo.
//
// Este módulo es la ÚNICA fuente de verdad reactiva. Dos reglas duras:
//
//   1. DICTAMEN EXPLÍCITO, JAMÁS SOSPECHA. La regla no cambió en F6.8 T5 ni en
//      REMATE-266; lo que creció es la lista de HECHOS que cuentan como
//      dictamen. Son cinco, y ninguno es una sospecha:
//        · `online`/`offline` de window — los MISMOS eventos que el vigía de
//          F6.7a ya escuchaba (hoy el vigía escucha ESTE bus, no window);
//        · el sensor nativo (@capacitor/network) diciendo `connected:false` en
//          el arranque — la SEMILLA, y solo hacia abajo (T5-1);
//        · los avisos de cambio del mismo plugin, en ambos sentidos (T5-2);
//        · una respuesta HTTP exitosa — la EVIDENCIA hacia arriba, y la prueba
//          más dura de todas: un servidor contestó (T5-4);
//        · DOS peticiones seguidas que NUNCA LLEGARON al servidor dentro de una
//          ventana corta — la EVIDENCIA hacia abajo (REMATE-266, abajo).
//      Lo que sigue prohibido es lo de siempre: `navigator.onLine` no
//      *establece* nada (WKWebView miente seguido; la doctrina vive en
//      converge.js:165-169 y converge.js lo usa solo como pista). Sin ninguno
//      de los cinco hechos el veredicto es UNKNOWN, y UNKNOWN se comporta
//      exactamente como hoy: nadie pinta nada nuevo, nadie toma atajos.
//
//      POR QUÉ HIZO FALTA EL QUINTO (incidente del 2026-08-16, iPhone en
//      carretera con la señal oscilando SOS↔LTE): los cuatro primeros hechos
//      modelan la ANTENA —lo que el sistema operativo declara— y ninguno modela
//      EL INTERNET. Con red zombie ("lie-fi") el sistema reporta interfaz
//      conectada mientras toda petición muere, así que el veredicto no podía
//      bajar por ningún camino: el pill jamás encendía, los candados offline de
//      las pantallas quedaban abiertos y la app se pasaba minutos esperando
//      fantasmas. El quinto hecho es la única evidencia que distingue las dos
//      cosas, y es tan dura como su hermana de arriba: una petición que se
//      intentó y NO LLEGÓ.
//
//   2. CERO TIMERS. Igual que la convergencia: esto reacciona, no vigila. El
//      único setTimeout del módulo es el techo POR LLAMADA del sensor nativo, y
//      se cancela en cuanto la llamada termina.
//
// APP-ONLY, y esto es load-bearing: `online`/`offline` los despacha CUALQUIER
// navegador, así que sin la guardia de IS_APP_BUILD la web habría estrenado un
// chip de offline y CTAs deshabilitados que hoy no tiene. La cara confiable de
// este sprint es del build nativo; la web se queda con server-status y punto.
// La guardia va aquí, en el único lugar que escucha, y no repartida por cada
// pantalla — así ninguna superficie nueva puede olvidarla.
//
// Por qué NO vive dentro de converge.js: la tubería de F6.7a es de consumo, no
// de modificación (regla del sprint). Este módulo escucha la misma señal desde
// afuera; el vigía sigue siendo el dueño de despertar al cartero.

import { IS_APP_BUILD } from '../buildTarget';
import { log as diagLog } from '../diag/readyChainLog';

export const NET_VERDICT = {
  UNKNOWN: 'unknown', // todavía no llegó ningún evento: se comporta como hoy
  UP: 'up',
  DOWN: 'down',
};

// ── F6.8 T5 — FLAG DE RECULE DE LA SEMILLA NATIVA ───────────────────────────
//
// LA DOCTRINA, en una línea: apagar esto es CERO SEMILLA, CERO PLUGIN, CERO
// EVIDENCIA —ni la de arriba ni la de abajo— y el módulo vuelve bit a bit a lo
// de hoy (dos oídos de `window` y nada más), y con él vuelven el arranque frío
// ciego, la ceguera ante la red zombie y todo lo que cuelga de
// UNKNOWN. No hace falta desinstalar el paquete ni tocar otra línea: sin este
// `true`, `@capacitor/network` no se importa nunca (el import es dinámico y
// vive detrás de este gate), la semilla no se siembra, el listener no se
// registra y la evidencia HTTP no escribe. Tampoco hay que desinstalar el
// paquete: sin este `true` el chunk nunca se pide.
//
// Por qué existe: la T5 estrena la PRIMERA dependencia nativa de red del
// proyecto y el censo (H-4) dejó escrito que no hay una sola línea en el repo
// que diga qué contesta el plugin en el segundo cero. Si en device miente —el
// caso caro es "OFFLINE con señal viva", que bloquea el login (censo D.1)— la
// vuelta atrás debe ser una línea, no un sprint.
export const NETWORK_NATIVE_SEED = true;

// Techo duro POR LLAMADA para cualquier operación del plugin de red. Mismo
// racional y mismo valor que NATIVE_PLUGIN_TIMEOUT_MS (warehouse/connection.js)
// y NATIVE_KEYCHAIN_TIMEOUT_MS (tokenStore.js): si el bridge no contesta, la
// operación DEGRADA (aquí: se queda en UNKNOWN, o sea lo de hoy) en vez de
// dejar una promesa colgada. Helper PROPIO a propósito — importar connection.js
// arrastraría la bodega entera al grafo de este módulo, que hoy no depende de
// nada más que buildTarget.
const NATIVE_NET_TIMEOUT_MS = 5000;

// Espejo del flag SOLO para tests (hermano de __resetNetworkVerdictForTests):
// permite fijar el estado APAGADO sin tocar la constante de arriba, que es la
// que Carlos apaga de verdad para recular. null = manda la constante.
let seedFlagOverrideForTests = null;

// ¿Estamos DENTRO de un shell nativo de Capacitor? Mismo detector que
// tokenStore.js:40-42 y connection.js:47-49, y evaluado POR LLAMADA, no al
// cargar el módulo: los tests instalan y quitan el global entre casos.
function isNativePlatform() {
  return typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
}

// El gate ÚNICO de todo lo que la T5 estrena: build de app, flag encendida Y
// shell nativo de verdad. Que sea uno solo es load-bearing por la misma razón
// que el `if` de abajo está en el único lugar que escucha: una superficie nueva
// no puede olvidarlo.
//
// POR QUÉ EL TERCER TÉRMINO, y no es higiene de tests: `@capacitor/network`
// trae una implementación WEB, y esa implementación lee `navigator.onLine`.
// Sin este término, un build de app abierto en un navegador (o cualquier
// entorno sin shell) sembraría el veredicto DESDE navigator.onLine — que es
// exactamente lo que la regla 1 de este módulo prohíbe y lo que
// networkVerdict.test.js:31-33 fija como prohibición desde F6.7b. El sensor
// que la T5 estrena es el NATIVO; su gemelo web no dictamina nada.
function nativeNetSensorEnabled() {
  const flag = seedFlagOverrideForTests === null ? NETWORK_NATIVE_SEED : seedFlagOverrideForTests;
  return IS_APP_BUILD && flag && isNativePlatform();
}

// Techo por llamada, y SE LIMPIA. La diferencia con los dos precedentes
// (connection.js:51-55, tokenStore.js:51-60) es deliberada: aquellos dejan el
// setTimeout vivo hasta que expira; este lo cancela en cuanto la carrera
// termina. Motivo: la regla de batería de este sprint (cero timers persistentes)
// y su test — `jest.getTimerCount() === 0` tras la semilla y tras un tap.
function withNativeNetTimeout(promise, label) {
  let timer = null;
  return Promise.race([
    promise,
    new Promise((resolve, reject) => {
      timer = setTimeout(
        () => reject(new Error(`${label} timed out after ${NATIVE_NET_TIMEOUT_MS}ms`)),
        NATIVE_NET_TIMEOUT_MS
      );
    }),
  ]).finally(() => {
    if (timer !== null) { clearTimeout(timer); timer = null; }
  });
}

// Acceso perezoso al plugin. Import DINÁMICO (patrón tokenStore): webpack lo
// parte en un chunk que el bundle web jamás pide, y aquí además nunca se llega
// a pedir porque el gate es app-only.
//
// DEVUELVE UN OBJETO PLANO, NUNCA EL PLUGIN PELADO — esto es load-bearing y la
// trampa está documentada en tokenStore.js: `Network` es un proxy de
// registerPlugin que convierte CUALQUIER acceso a propiedad —incluido `then`—
// en llamada nativa. Si el proxy fuera el valor de resolución de una promesa,
// la asimilación de thenables leería `.then`, iOS respondería "not implemented"
// y la promesa que espera NO SE RESOLVERÍA JAMÁS (el hang de boot de F4.2c).
async function getNetworkPlugin() {
  const mod = await import('@capacitor/network');
  return { plugin: mod.Network };
}

// UNA consulta de estado al sensor nativo, bajo techo. Devuelve el
// ConnectionStatus del plugin, o null si el gate está cerrado / el puente
// falla / no contesta a tiempo. Compartido por la SEMILLA y por la re-consulta
// del portero de Login/Signup: un solo camino, un solo techo, cero polling.
async function readNativeStatus() {
  if (!nativeNetSensorEnabled()) return null;
  try {
    const status = await withNativeNetTimeout(
      (async () => {
        const { plugin } = await getNetworkPlugin();
        return plugin.getStatus();
      })(),
      'Network.getStatus'
    );
    return status || null;
  } catch (e) {
    // Degradación deliberada: sin respuesta del sensor el veredicto se queda
    // donde estaba (en el arranque, UNKNOWN = exactamente lo de hoy).
    return null;
  }
}

let verdict = NET_VERDICT.UNKNOWN;
const listeners = new Set();

function emit() {
  // Copia defensiva: un suscriptor que se desuscribe dentro de su propio
  // callback no debe romper la iteración.
  for (const cb of Array.from(listeners)) {
    try { cb(verdict); } catch (e) { /* un suscriptor roto no calla a los demás */ }
  }
}

/* EL EMBUDO ÚNICO, y desde REMATE-266 también LA BITÁCORA.
 *
 * `NET.VERDICT {de, a, fuente}` es la línea 3 de las cuatro que el recon del
 * congelamiento pidió (§4.2). Hasta hoy `startNetworkWatch` escribía NET.UP /
 * NET.DOWN —el QUÉ— pero nadie escribía el QUIÉN, y esa es justo la distinción
 * que separa "el plugin dijo que había red" de "una respuesta 2xx escribió UP"
 * de "dos peticiones murieron sin llegar". Sin ella, un registro con red zombie
 * y otro con modo avión se leen igual.
 *
 * `fuente` vale 'window' | 'plugin' | 'semilla' | 'evidencia'. Se escribe DENTRO
 * del amortiguador (después del `if` de valor repetido), así que una fuente que
 * confirma lo que ya se sabía no gasta una entrada del anillo.
 */
function set(next, fuente) {
  if (verdict === next) return;
  const de = verdict;
  verdict = next;
  try { diagLog('NET.VERDICT', { de, a: next, fuente: fuente || null }); } catch (e) { /* el visor jamás decide un veredicto */ }
  emit();
}

/* ── REMATE-266 — LA MÁQUINA DE LA EVIDENCIA HACIA ABAJO ─────────────────────
 *
 * DOS fallos con forma de red dentro de 10 s, sin ningún 2xx intercalado, y el
 * veredicto baja. Los tres números son deliberados:
 *
 *   · DOS, no uno. Una petición sola puede morir por mil razones que no son la
 *     red (un endpoint que tarda, un reinicio del backend, un abort de otro
 *     mecanismo). Dos seguidas sin una sola respuesta buena en medio ya no es
 *     mala suerte. Bajo lie-fi el par llega en segundos —cualquier pantalla
 *     lanza más de dos peticiones al montar— y ese es exactamente el caso que
 *     esto existe para cazar.
 *   · DIEZ SEGUNDOS, que es el techo de una petición normal
 *     (`DEFAULT_TIMEOUT_MS`): dos fallos separados por más que eso pertenecen a
 *     episodios distintos y no se suman.
 *   · CUALQUIER 2xx BORRA LA CUENTA. La evidencia de arriba es más dura que la
 *     de abajo y le gana siempre: un servidor que contestó prueba que hay
 *     internet, punto.
 *
 * QUÉ CUENTA COMO FALLO, y esto es más estrecho de lo que parece: SOLO los que
 * nunca llegaron al servidor. La frontera y su porqué viven en api.js (EH-2,
 * `isOfflineShapedError`), que es quien alimenta esta máquina; aquí no se
 * clasifica nada.
 */
export const EVIDENCE_DOWN_WINDOW_MS = 10_000;
export const EVIDENCE_DOWN_COUNT = 2;

// Marcas de tiempo de los fallos vivos de la ventana. Array plano y corto (a lo
// sumo EVIDENCE_DOWN_COUNT entradas): cero timers, cero estado que limpiar.
let fallosDeEvidencia = [];

function ahora() {
  try { return Date.now(); } catch (e) { return 0; }
}

/**
 * F6.8 T5-1 — LA SEMILLA, y SOLO HACIA ABAJO.
 *
 * EL HUECO QUE CIERRA (censo A.1/A.5): `online`/`offline` son eventos de
 * TRANSICIÓN. Una app que ARRANCA con el avión ya puesto no ve ninguna, así que
 * el veredicto se queda en UNKNOWN todo el proceso: el pill no aparece nunca,
 * las seis superficies lanzan su petición condenada, y el candado D-G solo se
 * evalúa tarde, cuando el dato ya trae la evidencia del fallo.
 *
 * ASIMÉTRICA A PROPÓSITO, y esta es la firma de Carlos, no una optimización:
 *   · `connected === false` → set(DOWN). El sensor afirmando "sin señal" es la
 *     única lectura que este módulo acepta como dictamen de arranque.
 *   · `connected === true` → NADA. Queda UNKNOWN, o sea el comportamiento de
 *     hoy, entero. Sembrar UP sería la mentira CARA del censo (D.1b): apagaría
 *     el candado D-G ignorando `data.offline` (networkVerdict.js:109) y le
 *     daría la vuelta al default de Flashcards. Un UP que no sirve no cuesta
 *     nada aquí; un UP mentiroso reabre el agujero S11 que la T7-P3 cerró.
 *   · error / sin respuesta a tiempo → NADA. Igual que hoy.
 *
 * FIRE-AND-FORGET: nadie la espera. Si aterriza tarde y la petición de arranque
 * ya salió, el S-1 de api.js la aborta al recibir el DOWN — comportamiento que
 * ya existe y que aquí no se toca.
 */
export async function seedNetworkVerdictFromNative() {
  const status = await readNativeStatus();
  if (status && status.connected === false) set(NET_VERDICT.DOWN, 'semilla');
}

/**
 * F6.8 T5-2 — EL PLUGIN SIGUE HABLANDO DESPUÉS DEL ARRANQUE.
 *
 * La semilla es una foto del segundo cero; esto es el oído permanente. Sus
 * avisos escriben el veredicto en AMBOS sentidos (a diferencia de la semilla),
 * porque aquí sí hay una TRANSICIÓN observada por el sistema operativo, que es
 * exactamente la clase de evidencia que este módulo siempre aceptó de `window`.
 *
 * Por el MISMO embudo `set()`: un valor repetido no re-emite, así que el par
 * plugin + `window` reportando lo mismo cuesta una sola emisión. Sin timers
 * propios y sin handle guardado — este oído vive lo que vive el proceso, igual
 * que E-1/E-2.
 */
async function startNativeNetworkListener() {
  if (!nativeNetSensorEnabled()) return;
  try {
    const { plugin } = await getNetworkPlugin();
    await plugin.addListener('networkStatusChange', (status) => {
      if (!status) return;
      set(status.connected ? NET_VERDICT.UP : NET_VERDICT.DOWN, 'plugin');
    });
  } catch (e) {
    // Sin listener el módulo queda con los dos oídos de `window` de siempre.
  }
}

/**
 * F6.8 T5-4 — EL TERCER ESCRITOR: LA EVIDENCIA.
 *
 * Una respuesta HTTP exitosa es la prueba más dura de red que existe en esta
 * app: no es una pista (`navigator.onLine`), ni un aviso del sistema (`online`,
 * que iOS despacha cuando hay INTERFAZ y no cuando hay ruta — converge.js:104),
 * es un servidor que contestó. Escribe UP por el mismo embudo.
 *
 * NO cambia quién sale ni cuándo: el cartero y el downloader siguen sin
 * consultar el veredicto para decidir si salen. Esto solo corrige el veredicto
 * DESPUÉS del hecho — y es el antídoto natural del riesgo D.1a del censo (un
 * DOWN mentiroso que nadie corrige).
 */
export function markNetworkEvidenceUp() {
  if (!nativeNetSensorEnabled()) return;
  // REMATE-266: un 2xx borra la cuenta de fallos ANTES de escribir. Va aquí
  // dentro y no en el llamador para que las dos mitades de la evidencia —la de
  // arriba y la de abajo— no puedan quedar desincronizadas por descuido.
  fallosDeEvidencia = [];
  set(NET_VERDICT.UP, 'evidencia');
}

/**
 * REMATE-266 — EL CUARTO ESCRITOR: LA EVIDENCIA HACIA ABAJO.
 *
 * Simétrico de `markNetworkEvidenceUp`, y el que le faltaba al módulo: hasta
 * hoy los TRES escritores de DOWN (el `offline` de window, la semilla y el
 * aviso del plugin) dependían todos de que el sistema operativo declarara la
 * interfaz caída. Con red zombie el sistema declara lo contrario, así que no
 * existía un solo camino por el que el veredicto pudiera bajar por más que
 * TODAS las peticiones murieran. Eso es lo que este escritor cierra.
 *
 * NO ES UNA SOSPECHA, y ahí está la línea que la regla 1 no cruza: no se
 * consulta `navigator.onLine`, no se adivina y no se cuenta un fallo que trajo
 * respuesta del servidor. Lo que se cuenta son peticiones que se INTENTARON y
 * no llegaron — el mismo tipo de hecho que un `offline` de window, sólo que
 * observado por nosotros en vez de por el sistema.
 *
 * FIRE-AND-FORGET Y SIN TIMERS: la ventana se evalúa con los propios sellos de
 * tiempo de los fallos, así que el módulo sigue cumpliendo su regla 2.
 */
export function markNetworkEvidenceDown() {
  if (!nativeNetSensorEnabled()) return;
  const t = ahora();
  fallosDeEvidencia = fallosDeEvidencia.filter((prev) => t - prev <= EVIDENCE_DOWN_WINDOW_MS);
  fallosDeEvidencia.push(t);
  if (fallosDeEvidencia.length < EVIDENCE_DOWN_COUNT) return;
  // Se vacía ANTES de escribir: el próximo DOWN por evidencia vuelve a exigir
  // su par completo, así que un episodio largo no se re-dictamina a cada fallo.
  fallosDeEvidencia = [];
  set(NET_VERDICT.DOWN, 'evidencia');
}

/**
 * F6.8 T5-5(b) — LA RE-CONSULTA DEL PORTERO.
 *
 * El portero pre-submit de Login/Signup es el ÚNICO punto donde un veredicto
 * DOWN mentiroso BLOQUEA una acción del usuario en vez de degradar una lectura
 * (censo D.1a). Su escape es una consulta fresca al sensor en el instante del
 * tap: UNA, con el mismo techo de 5 s, cero polling y cero estado nuevo.
 *
 * `connected === true` → escribe UP (y con eso se destraban solos el pill, el
 * banner y los botones) y devuelve true: el submit pasa.
 * Cualquier otra cosa —offline confirmado, error, sin respuesta, flag apagada,
 * plugin ausente, web— devuelve false y el portero rechaza con el toast de hoy,
 * byte-idéntico.
 */
export async function recheckNativeNetworkUp() {
  const status = await readNativeStatus();
  if (status && status.connected === true) {
    fallosDeEvidencia = [];
    set(NET_VERDICT.UP, 'plugin');
    return true;
  }
  return false;
}

/**
 * F6.8 T5-6 — EL PORTERO, UNA SOLA COPIA PARA LAS CUATRO PUERTAS.
 *
 * Las dos pantallas de auth tienen CUATRO entradas que un veredicto DOWN puede
 * cerrar: Entrar, Crear cuenta y los dos "Continuar como invitado". Son los
 * únicos puntos de la app donde un DOWN equivocado BLOQUEA al usuario en vez de
 * degradar una lectura (censo D.1a): sin red no puede entrar, y si el sensor
 * mintió tampoco. Por eso las cuatro hacen la MISMA pregunta y con una sola
 * copia — cuatro copias sueltas eran cuatro oportunidades de que la próxima
 * puerta se quedara atrás (así nació el C-16 de la T5).
 *
 * La pregunta: si el veredicto NO dice DOWN, no hay nada que preguntar y la
 * acción procede (cero consultas al sensor con UNKNOWN o UP — el
 * comportamiento de hoy, entero). Si dice DOWN, se le pregunta al sensor nativo
 * en ese instante: UNA consulta, techo de 5 s, cero polling, cero estado nuevo.
 * "Conectado" corrige el veredicto a UP (con eso el banner se destraba solo) y
 * devuelve true. Offline confirmado, error, sin respuesta, flag de recule
 * apagada, plugin ausente o web → false.
 *
 * `false` NO pinta nada: el toast lo pone el llamador, porque el texto cambia
 * por pantalla (login vs. signup).
 *
 * NO ES `async`, Y ESO ES LOAD-BEARING. Sin dictamen DOWN devuelve `true`
 * SÍNCRONO, no una promesa resuelta: así el camino por defecto —UNKNOWN o UP,
 * o sea toda la web y la sesión normal de cualquiera— no estrena ni un
 * microtask, y el `setLoading(true)` del llamador sigue cayendo en el MISMO
 * tick que el tap, byte por byte como hoy. Con `async` no: `await` difiere
 * SIEMPRE, aunque el valor ya esté ahí. De ahí el modismo del llamador
 * (`gate !== true && !(await gate)`), que es feo a propósito: el atajo es
 * deliberado, no un descuido. Solo el caso DOWN paga la espera, que es el
 * único que tiene algo que esperar.
 *
 * Requerimiento pendiente: los botones nativos de Iniciar con Google e Iniciar
 * con Apple deberán pasar por este mismo portero cuando entren; no implementar
 * aquí.
 *
 * VIVE AQUÍ DESDE LA T5c (C-20), y antes vivía en `LoginPage.js` con SignupPage
 * importándolo pantalla→pantalla. Lo frágil era ese import, no el lugar: aquí
 * el portero queda pegado a lo único que usa (`recheckNativeNetworkUp` y, por
 * debajo, el gate y la flag de recule), y las dos pantallas lo piden del mismo
 * módulo del que ya piden el veredicto. Sigue siendo POLÍTICA de una superficie
 * —qué hace auth con un DOWN— y no una regla del veredicto: nada de este módulo
 * lo llama.
 *
 * @returns {true | Promise<boolean>} `true` síncrono si no hay nada que
 *   preguntar; si no, la promesa de la segunda opinión.
 */
export function ensureNetworkForAuthAction(isOffline) {
  if (!isOffline) return true;
  return recheckNativeNetworkUp();
}

/**
 * Arranque del sensor nativo: la semilla y el oído del plugin, juntos y bajo el
 * mismo gate. Se dispara desde la CARGA DEL MÓDULO (el punto más temprano del
 * arranque — censo D.3 paso 2), fire-and-forget: nadie lo espera y ninguna de
 * las dos ramas puede rechazar (las dos se tragan su error).
 *
 * Exportada solo para que los tests puedan fijar el estado APAGADO del flag.
 */
export function startNativeNetworkSensor() {
  if (!nativeNetSensorEnabled()) return;
  seedNetworkVerdictFromNative();
  startNativeNetworkListener();
}

if (IS_APP_BUILD && typeof window !== 'undefined') {
  window.addEventListener('online', () => { fallosDeEvidencia = []; set(NET_VERDICT.UP, 'window'); });
  window.addEventListener('offline', () => set(NET_VERDICT.DOWN, 'window'));
  // F6.8 T5: el sensor nativo entra por AQUÍ, dentro del mismo paraguas y en el
  // mismo instante que los dos oídos de window. En web esta línea no corre
  // jamás — el veredicto sigue siendo UNKNOWN para siempre, que es la propiedad
  // de la que cuelgan OfflinePill, Flashcards, LoginPage y sus tests.
  startNativeNetworkSensor();
}

/** El veredicto vigente. UNKNOWN mientras no haya llegado un evento. */
export function getNetworkVerdict() {
  return verdict;
}

/**
 * ¿El vigía DICTAMINÓ que no hay señal? Solo true con un `offline` recibido —
 * nunca por sospecha, nunca por UNKNOWN. Es la condición del atajo offline:
 * las pantallas que hoy esperan a que el HTTP agote su timeout pueden pintar lo
 * local de inmediato SOLO con este true en la mano.
 */
export function isExplicitlyOffline() {
  return verdict === NET_VERDICT.DOWN;
}

/**
 * RPW T7 (P3) — ¿ESTÁ SIN SEÑAL, PARA EFECTOS DE UN CANDADO?
 *
 * EL HALLAZGO: `isExplicitlyOffline()` solo dice `true` con un evento `offline`
 * RECIBIDO, y los eventos son de TRANSICIÓN. Una app que ARRANCA con el avión ya
 * puesto no ve ninguna transición: el veredicto se queda en UNKNOWN todo el
 * proceso, y cualquier candado colgado de esa función simplemente NO SE EVALÚA.
 * Así se coló el S11 del smoke — el invitado mataba la app y el arranque nuevo
 * nacía sin veredicto, así que el candado D-G ni se preguntaba.
 *
 * La regla 1 del módulo (dictamen, jamás sospecha) NO se toca: aquí no se
 * consulta `navigator.onLine` ni se adivina nada. Lo que se agrega es la segunda
 * EVIDENCIA que las pantallas ya tienen en la mano — `data.offline`, que los
 * ensambladores de bodega ponen cuando el fetch vivo CAYÓ con forma-de-red
 * (F6.4b). Eso no es una sospecha: es una petición que ya se intentó y no llegó.
 *
 * La precedencia es la del vigía, siempre:
 *   · DOWN  → sin señal (lo de hoy);
 *   · UP    → CON señal, aunque el dato traiga un `offline` viejo y pegado — un
 *             veredicto fresco le gana a la foto de cuando se cargó la pantalla,
 *             y la duda se resuelve a favor del usuario;
 *   · UNKNOWN → manda la evidencia del propio dato.
 *
 * `isExplicitlyOffline()` se queda intacta y con sus consumidores: los ATAJOS
 * (pintar de bodega sin esperar el timeout) solo pueden colgar de un dictamen
 * explícito. Esto es para los CANDADOS, que es la pregunta contraria.
 *
 * REMATE-266 — ESTA FUNCIÓN NO CAMBIÓ, Y ES UNA DECISIÓN, NO UN OLVIDO.
 *
 * El riesgo D.1a que la nota de arriba describe (un UP añejo apagando candados
 * con la pantalla ya en fallo) se evaluó para generalizar a TODAS las
 * superficies el escudo que hoy sólo tiene el portero de Login/Signup, o sea
 * invertir la precedencia y dejar que `dataOffline` le gane a un UP. NO se
 * hizo, y el porqué es que esa inversión reabre el bug que la precedencia
 * actual existe para cerrar: el chip y los controles PEGADOS. `dataOffline` es
 * una FOTO del instante en que se armó el dato; el veredicto es el AHORA. Con
 * la inversión, una pantalla ensamblada sin señal se quedaría con sus candados
 * puestos aunque la señal ya hubiera vuelto y el veredicto lo supiera, y sólo
 * navegar la destrabaría — exactamente el síntoma de F6.7b.
 *
 * Lo que sí cierra el hueco es la otra mitad: con la evidencia hacia abajo
 * (`markNetworkEvidenceDown`) un UP añejo ya no puede sobrevivir a una pantalla
 * en fallo, porque las dos primeras peticiones muertas lo tumban. El candado
 * pasa a evaluarse contra un veredicto FRESCO, que es lo que la precedencia de
 * arriba siempre supuso y hasta hoy no era cierto bajo red zombie.
 */
export function isOfflineForGate(dataOffline) {
  if (verdict === NET_VERDICT.DOWN) return true;
  if (verdict === NET_VERDICT.UP) return false;
  return Boolean(dataOffline);
}

/**
 * Suscripción al veredicto. Devuelve la función para desuscribirse.
 * No dispara al suscribirse: el consumidor ya leyó getNetworkVerdict().
 */
export function subscribeNetworkVerdict(cb) {
  if (typeof cb !== 'function') return () => {};
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Solo para tests: devuelve el módulo a su estado de arranque. */
export function __resetNetworkVerdictForTests() {
  verdict = NET_VERDICT.UNKNOWN;
  listeners.clear();
  seedFlagOverrideForTests = null;
  fallosDeEvidencia = [];
}

/**
 * Solo para tests: fija el estado del flag de recule sin tocar la constante.
 * `null` devuelve el mando a NETWORK_NATIVE_SEED.
 */
export function __setNetworkNativeSeedForTests(value) {
  seedFlagOverrideForTests = value;
}
